import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState, useEffect, useRef } from "react";
import { ArrowLeft, ChevronDown, ChevronRight, Copy, Loader2, Check, Pencil } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getPolicy, getPolicyImpact } from "@/lib/policy-library.functions";
import { RecentDecisions } from "@/components/policy-surface/recent-decisions";
import { labelForCapability } from "@/components/policy-library/policy-library-view";
import { resolveEditTarget } from "@/components/policy-library/policy-library-view";
import { trackPolicyEvent } from "@/lib/policy-telemetry";

export const Route = createFileRoute("/_authenticated/app/policies/$policyId")({
  component: PolicyDetailPage,
});

function PolicyDetailPage() {
  const { policyId } = useParams({ from: "/_authenticated/app/policies/$policyId" });
  const getFn = useServerFn(getPolicy);
  const impactFn = useServerFn(getPolicyImpact);
  const { data, isLoading, error } = useQuery({
    queryKey: ["policy-detail", policyId],
    queryFn: () => getFn({ data: { policyId } }),
  });
  const { data: impact, isLoading: impactLoading } = useQuery({
    queryKey: ["policy-impact", policyId],
    queryFn: () => impactFn({ data: { policyId } }),
    staleTime: 30_000,
  });
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Dwell-time + view telemetry. Logs on unmount with ms-on-page.
  const openedAt = useRef<number>(Date.now());
  useEffect(() => {
    openedAt.current = Date.now();
    trackPolicyEvent("policy.detail.view", {
      source: "global",
      props: { policy_id: policyId },
    });
    return () => {
      trackPolicyEvent("policy.detail.dwell", {
        source: "global",
        props: {
          policy_id: policyId,
          dwell_ms: Date.now() - openedAt.current,
        },
      });
    };
  }, [policyId]);

  if (isLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading policy…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="p-6 space-y-2">
        <p className="text-sm text-destructive">Policy not found.</p>
        <Link to="/app/policies" className="text-sm underline">Back to Library</Link>
      </div>
    );
  }

  const p = data.policy;
  const capabilities = Array.from(new Set(p.canonical_targets.map((t) => t.capability)));
  const scopeLabel =
    p.scope_level === "customer"
      ? `${p.customer_name ?? "Customer"} only`
      : p.scope_level === "facility"
        ? "Facility"
        : "Tenant-wide — all customers";

  return (
    <div className="p-4 sm:p-6 max-w-4xl space-y-6">
      <div>
        <Link to="/app/policies" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Policy Library
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{p.title}</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CopyableId id={p.policy_id} />
            <span>·</span>
            <StatusPill status={p.status} />
            <span>·</span>
            <span>{scopeLabel}</span>
          </div>
        </div>
        <EditPolicyButton policy={p} />
      </div>

      {/* Four-questions block */}
      <div className="rounded-lg border bg-card p-5 space-y-4">
        {p.summary && (
          <p className="text-sm leading-relaxed">{p.summary}</p>
        )}
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 text-sm">
          <Field label="When does this apply?" value={p.intent_prompt ? quote(p.intent_prompt) : derivedWhen(p.primary_capability)} />
          <Field label="What happens?" value={derivedWhat(p.primary_capability)} />
          <Field label="Who is affected?" value={scopeLabel} />
          <Field label="Why does this exist?" value={p.source === "manual" ? "Manually authored." : p.intent_prompt ?? "AI-proposed policy."} />
        </dl>
      </div>

      {/* Preview Impact — canonical evaluator */}
      <PreviewImpactBlock impact={impact?.preview} loading={impactLoading} />

      {/* Recent Decisions */}
      <RecentDecisions
        rows={(impact?.decisions ?? []).map((d) => ({
          id: d.id,
          at: d.at,
          summary: d.summary,
          detail: d.detail,
          href: d.href,
          tone: d.tone,
        }))}
        emptyHint={impactLoading ? "Loading…" : "No recent decisions in the last 90 days."}
      />

      {/* Underlying capabilities */}
      <div className="rounded-lg border bg-card p-5">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Underlying Capabilities</div>
        <div className="flex flex-wrap gap-2">
          {capabilities.map((c) => (
            <Badge key={c} variant="secondary" className="gap-1.5">
              <Check className="h-3 w-3" /> {labelForCapability(c)}
            </Badge>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          The systems this policy uses to do its job.
        </p>
      </div>

      {/* Lifecycle */}
      <div className="rounded-lg border bg-card p-5">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Lifecycle</div>
        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2 text-sm">
          <KV k="Created" v={fmt(p.created_at)} />
          <KV k="Last modified" v={fmt(p.updated_at)} />
          <KV k="Last evaluated" v={fmt(p.last_evaluated_at)} />
          <KV k="Last triggered" v={fmt(p.last_triggered_at)} />
          <KV k="Trigger count (30d)" v={String(p.trigger_count_30d)} />
          <KV k="Source" v={p.source.replaceAll("_", " ")} />
        </dl>
      </div>

      {/* Advanced */}
      <div className="rounded-lg border bg-card">
        <button
          onClick={() => setAdvancedOpen((v) => !v)}
          className="w-full flex items-center justify-between p-4 text-sm font-medium hover:bg-muted/40"
        >
          <span className="inline-flex items-center gap-1.5">
            {advancedOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Advanced — canonical targets & provenance
          </span>
          <span className="text-xs text-muted-foreground">{p.canonical_targets.length} record(s)</span>
        </button>
        {advancedOpen && (
          <div className="border-t p-4 space-y-3 text-xs font-mono">
            <div>Internal ID: <span className="text-muted-foreground">{p.id}</span></div>
            {p.intent_id && <div>Intent ID: <span className="text-muted-foreground">{p.intent_id}</span></div>}
            <div className="pt-2 space-y-1">
              {p.canonical_targets.map((t, i) => (
                <div key={i} className="rounded border bg-background px-3 py-2">
                  <div>{labelForCapability(t.capability)} <span className="text-muted-foreground">· {t.table}</span></div>
                  <div className="text-muted-foreground">{t.id}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1 leading-relaxed">{value}</dd>
    </div>
  );
}

/**
 * Routes the operator to the right place to edit this policy. Compileable
 * capabilities (hold_creation / hold_release) have a dedicated Advanced
 * editor where rules can be modified inline. Other capabilities fall back
 * to the capability lens of the Policy Library — never a dead-end.
 */
function EditPolicyButton({
  policy,
}: {
  policy: { primary_capability: string; canonical_targets: Array<{ id: string; table: string; capability: string }> };
}) {
  const target = resolveEditTarget(policy as never);
  if (target) {
    return (
      <Link to={target.to} search={target.search as never}>
        <Button variant="outline" size="sm" type="button" className="gap-1.5">
          <Pencil className="h-3.5 w-3.5" /> Edit policy
        </Button>
      </Link>
    );
  }
  return (
    <Link to="/app/policies" search={{ capability: policy.primary_capability }}>
      <Button variant="outline" size="sm" type="button" className="gap-1.5">
        <Pencil className="h-3.5 w-3.5" /> Manage in {labelForCapability(policy.primary_capability)}
      </Button>
    </Link>
  );
}

function PreviewImpactBlock({
  impact,
  loading,
}: {
  impact: Awaited<ReturnType<typeof getPolicyImpact>>["preview"] | undefined;
  loading: boolean;
}) {
  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="text-sm font-medium">Preview impact</h3>
          <p className="text-xs text-muted-foreground">What would happen if this policy ran now.</p>
        </div>
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </div>
      {!impact && !loading && (
        <p className="mt-3 text-sm text-muted-foreground">No preview available.</p>
      )}
      {impact?.kind === "unavailable" && (
        <p className="mt-3 text-sm text-muted-foreground">{impact.reason}</p>
      )}
      {impact?.kind === "hold_release" && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Stat label="Open holds in scope" value={String(impact.matching_now)} />
            <Stat label="Last 90 days" value={String(impact.matching_historical_90d)} />
          </div>
          <ExampleList examples={impact.examples} emptyHint="No matching open holds." />
        </div>
      )}
      {impact?.kind === "hold_creation" && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Stat label="Sample evaluated" value={String(impact.sample_size)} />
            <Stat label="Would match" value={String(impact.matching_in_sample)} />
            <Stat label="Match rate" value={`${Math.round(impact.match_rate * 1000) / 10}%`} />
          </div>
          <ExampleList examples={impact.examples} emptyHint="No matching orders in the recent sample." />
          <p className="text-xs text-muted-foreground">
            Evaluated against the most recent orders using the same logic that will run at order import.
          </p>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function ExampleList({
  examples,
  emptyHint,
}: {
  examples: Array<{ id: string; label: string; reason: string; href: string }>;
  emptyHint: string;
}) {
  if (examples.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyHint}</p>;
  }
  return (
    <ul className="divide-y rounded-md border">
      {examples.map((e) => (
        <li key={e.id} className="flex items-center justify-between px-3 py-2 text-sm">
          <a href={e.href} className="hover:underline">{e.label}</a>
          <span className="text-xs text-muted-foreground">{e.reason}</span>
        </li>
      ))}
    </ul>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{k}</dt>
      <dd className="mt-0.5">{v}</dd>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === "active"
      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
      : status === "paused"
        ? "bg-muted text-muted-foreground"
        : status === "draft" || status === "proposed"
          ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
          : "bg-muted text-muted-foreground";
  return (
    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${tone}`}>
      {status}
    </span>
  );
}

function CopyableId({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(id);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="inline-flex items-center gap-1 rounded-sm bg-muted/60 px-1.5 py-0.5 font-mono text-[11px] hover:bg-muted"
    >
      {id} {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

function fmt(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function quote(s: string): string {
  return `"${s}"`;
}

function derivedWhen(capability: string): string {
  switch (capability) {
    case "hold_release":
      return "When a matching hold is open and its release condition is satisfied.";
    case "hold_creation":
      return "When a new order is imported and matches the trigger conditions.";
    default:
      return "When the capability evaluates this policy.";
  }
}

function derivedWhat(capability: string): string {
  switch (capability) {
    case "hold_release":
      return "Releases the matching hold automatically.";
    case "hold_creation":
      return "Places a hold on the order before it can move forward.";
    default:
      return "Executes the configured effects.";
  }
}